import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useContext } from 'react';

// ROLLBACK(last-price): (Mark)/(Last) 라벨 재활성화 시 복원.
// import { isMarkTriggerPriceType } from '@flipster/apis/v2/trade/types';
import { T } from '@flipster/i18n';
import { useOverlayItem } from '@flipster/overlay';
import type { PswapOrder } from '@flipster/r230522/model/prex.v2.r230522.tables.private_orders';
import { SubAccountIdContext } from '@flipster/r230522/normal-sub/sub-account';
import { Button } from '@flipster/react/design-system/button';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import Formatted from '@flipster/react/design-utils/formatted';
import { hstack, stack } from '@flipster/styled-system/patterns';
import Num from '@flipster/utils/num';

import { SymbolScope } from '../../shared/symbol/bunja';
import { useDeleteTpSl } from '../../shared/trade-actions';
import { tradingSessionCheckBunja } from '../../shared/trading-suspension/state';
import { SelectedTpslOrderScope, selectedTpslOrderBunja } from '../state';
import { tpslManageSegmentBunja } from './segment';
import { tpslManageModalBunja } from './state';

interface Props {
  order: PswapOrder;
  onClickModify: (tpslOrder: PswapOrder) => void;
}

export const TpslOrderItem = ({ order, onClickModify }: Props) => {
  const overlay = useOverlayItem();
  const { positionUnitAtom, pricePrecisionAtom, tradeQtyPrecisionAtom, symbol, tpslType, tpslOrdersAtom } =
    useBunja(tpslManageModalBunja);
  const {
    tpslTriggerPriceAtom,
    // tpslTriggerPriceTypeAtom, // ROLLBACK(last-price)
    tpslOrderQtyAtom,
    tpslQtyPercentAtom,
    tpslOrderTypeAtom,
    tpslLimitPriceAtom,
    tpslIsBboAtom,
  } = useBunja(selectedTpslOrderBunja, [SelectedTpslOrderScope.bind(order)]);
  const { trackTpslSettingPartialSlotClick, trackTpslRemoveDialogView, trackTpslRemoveDialogClick } =
    useBunja(tpslManageSegmentBunja);
  const { checkTradable } = useBunja(tradingSessionCheckBunja, [SymbolScope.bind(symbol)]);
  const accountId = useContext(SubAccountIdContext);
  const { deleteTpslQuery } = useDeleteTpSl();
  const { mutateAsync: deleteTpsl } = deleteTpslQuery;

  const tpslOrders = useAtomValue(tpslOrdersAtom);
  const tpslTriggerPrice = useAtomValue(tpslTriggerPriceAtom);
  // const tpslTriggerPriceType = useAtomValue(tpslTriggerPriceTypeAtom); // ROLLBACK(last-price)
  const tpslOrderQty = useAtomValue(tpslOrderQtyAtom);
  const tpslQtyPercent = useAtomValue(tpslQtyPercentAtom);
  const tpslOrderType = useAtomValue(tpslOrderTypeAtom);
  const tpslLimitPrice = useAtomValue(tpslLimitPriceAtom);
  const tpslIsBbo = useAtomValue(tpslIsBboAtom);

  const positionUnit = useAtomValue(positionUnitAtom);
  const precision = useAtomValue(pricePrecisionAtom);
  const tradeQtyPrecision = useAtomValue(tradeQtyPrecisionAtom);

  return (
    <div className={stack({ gap: 16, px: 16, py: 12, bg: 'background.default.level-1', borderRadius: 12 })}>
      <dl
        className={stack({
          gap: 4,
          '& > div': { display: 'flex', justifyContent: 'space-between' },
          '& dt': { color: 'content.default.level-3', textStyle: 'Text/M/Regular' },
          '& dd': { color: 'content.default.level-5', textStyle: 'Text/M/Monospace/Regular' },
        })}
      >
        <div>
          <dt>
            <T id='Trade.r250401.Trigger_price' />
            {/* ROLLBACK(last-price): (Mark)/(Last) 라벨 — MM 준비 완료 시 재활성화.
            {' ('}
            <T
              id={
                isMarkTriggerPriceType(tpslTriggerPriceType)
                  ? 'Autogen_common.r251126.s_Mark'
                  : 'Common.r260623.Last_price_short'
              }
            />
            ) */}
          </dt>
          <dd>
            <Formatted.Number value={tpslTriggerPrice} precision={precision} toFixed />
          </dd>
        </div>
        <div>
          <dt>
            <T id='Autogen_common.r251126.s_Order_price' />
          </dt>
          <dd>
            {(() => {
              if (tpslOrderType !== 'Limit') return <T id='Trade.r251008.Market' />;
              if (tpslIsBbo) return <T id='Autogen_common.r251126.s_BBO' />;
              return (
                <Formatted.Number
                  value={tpslLimitPrice ? Num(tpslLimitPrice) : undefined}
                  precision={precision}
                  toFixed
                />
              );
            })()}
          </dd>
        </div>
        <div>
          <dt>
            <T id='Assets.r250401.Quantity' />
          </dt>
          <dd>
            <Formatted.Number value={tpslOrderQty} precision={tradeQtyPrecision} abs toFixed /> {positionUnit} (
            <Formatted.Percent value={tpslQtyPercent} toFixed precision={0} />)
          </dd>
        </div>
      </dl>
      <div className={hstack({ gap: 8 })}>
        <Button
          hierarchy='secondary'
          size='small'
          color='neutral'
          css={{ flex: 1 }}
          label={<T id='Common.r260303.Modify' />}
          onClick={() => {
            trackTpslSettingPartialSlotClick('Modify');
            onClickModify(order);
          }}
        />
        <Button
          hierarchy='secondary'
          size='small'
          color='neutral'
          css={{ flex: 1 }}
          label={<T id='Common.r250401.Remove' />}
          onClick={() => {
            if (!checkTradable()) return;
            trackTpslSettingPartialSlotClick('Remove');
            trackTpslRemoveDialogView(tpslType);

            dialog.open({
              title: {
                label: (
                  <T id={tpslType === 'tp' ? 'Position.r250401.Remove_tp_title' : 'Position.r250401.Remove_sl_title'} />
                ),
              },
              content: (
                <T
                  id={
                    tpslType === 'tp'
                      ? 'Position.r250401.Remove_tp_description'
                      : 'Position.r250401.Remove_sl_description'
                  }
                />
              ),
              buttons: {
                primary: (
                  <Dialog.Button
                    hierarchy='primary'
                    size='large'
                    label={<T id='Common.r250401.Confirm' />}
                    onClick={async () => {
                      trackTpslRemoveDialogClick('Confirm');
                      const isLast = tpslOrders.length === 1;
                      await deleteTpsl({ symbol, orderId: order.orderId, tpslType, accountId });
                      if (isLast) overlay.close();
                    }}
                  />
                ),
                secondary: (
                  <Dialog.Button
                    hierarchy='secondary'
                    color='neutral'
                    size='large'
                    label={<T id='Common.r250401.Cancel' />}
                    onClick={() => {
                      trackTpslRemoveDialogClick('Cancel');
                      dialog.close();
                    }}
                  />
                ),
              },
            });
          }}
        />
      </div>
    </div>
  );
};
